import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import HelpIcon from '../../../../../components/help/HelpIcon';

const WorkspaceUsersDialog = ({ open, workspace, onClose, fetchUsers, addUsers, removeUser }) => {
  const [users, setUsers] = useState([]);
  const [pendingUserId, setPendingUserId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !workspace) {
      setUsers([]);
      setPendingUserId('');
      setError(null);
      return;
    }

    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetchUsers?.(workspace);
        setUsers(response || []);
      } catch (err) {
        console.error('WorkspaceUsersDialog.load: error al obtener usuarios permitidos', err);
        setError(err.message || 'No fue posible cargar los usuarios permitidos.');
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [open, workspace, fetchUsers]);

  const handleAddUser = async () => {
    if (!pendingUserId.trim()) {
      setError('Debes ingresar un identificador de usuario');
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const updated = await addUsers?.(workspace, pendingUserId.trim());
      setUsers(updated || []);
      setPendingUserId('');
    } catch (err) {
      console.error('WorkspaceUsersDialog.handleAddUser: error', err);
      setError(err.message || 'No fue posible agregar el usuario.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    try {
      setIsLoading(true);
      setError(null);
      const updated = await removeUser?.(workspace, userId);
      setUsers(updated || []);
    } catch (err) {
      console.error('WorkspaceUsersDialog.handleRemoveUser: error', err);
      setError(err.message || 'No fue posible eliminar el usuario.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'var(--wb-bg-surface)',
          border: '1px solid var(--wb-border-16)'
        }
      }}
    >
      <DialogTitle sx={{ color: 'var(--wb-text-title)' }}>
        Usuarios permitidos
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: 'var(--wb-border)' }}>
        <TextField
          label="Identificador de usuario"
          placeholder="Ej. 27638d76598f42168e819e7bf2cdf010"
          fullWidth
          value={pendingUserId}
          disabled={isLoading}
          onChange={(event) => setPendingUserId(event.target.value)}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <HelpIcon module="modelo-semantico" topic="espacio-usuarios" anchor="identificador-de-usuario" />
                <IconButton
                  edge="end"
                  color="primary"
                  onClick={handleAddUser}
                  disabled={isLoading}
                >
                  <PersonAddAltIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiInputBase-root': {
              color: 'var(--wb-text-title)',
              backgroundColor: 'var(--wb-bg-overlay-90)'
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--wb-border-35)'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--wb-blue)'
            },
            '& .MuiInputLabel-root': {
              color: 'var(--wb-muted-80)'
            }
          }}
        />
        <Typography variant="caption" sx={{ color: 'var(--wb-muted-70)', mt: 1, display: 'block' }}>
          El propietario del espacio siempre se mantendrá con acceso incluso si no aparece en el listado.
        </Typography>

        <Box sx={{ mt: 3 }}>
          {error && (
            <Typography variant="body2" sx={{ color: 'var(--wb-danger)', mb: 2 }}>
              {error}
            </Typography>
          )}

          {users.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'var(--wb-muted-80)' }}>
              No hay usuarios adicionales registrados.
            </Typography>
          ) : (
            <List dense disablePadding>
              {users.map((user) => {
                const userId = user?.user_id || user?.userId || user?.id || '—';
                const ownerId =
                  workspace?.user_owner_id ||
                  workspace?.userOwnerId ||
                  workspace?.owner_id ||
                  workspace?.ownerId ||
                  null;
                const isOwner = ownerId ? userId === ownerId : false;
                return (
                  <ListItem
                    key={userId}
                    sx={{
                      borderRadius: 1,
                      border: '1px solid var(--wb-border)',
                      mb: 1,
                      bgcolor: 'var(--wb-x-bg-94a3b80a, rgba(148,163,184,0.04))'
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography sx={{ color: 'var(--wb-text)', fontFamily: 'monospace' }}>
                          {userId}
                        </Typography>
                      }
                      secondary={
                        isOwner ? 'Propietario del espacio' : undefined
                      }
                      secondaryTypographyProps={{
                        sx: { color: 'var(--wb-muted-70)', fontSize: '0.75rem' }
                      }}
                    />
                    {!isOwner && (
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveUser(userId)}
                          disabled={isLoading}
                          sx={{ color: 'var(--wb-danger)' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isLoading} sx={{ color: 'var(--wb-muted)' }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

WorkspaceUsersDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  workspace: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  fetchUsers: PropTypes.func,
  addUsers: PropTypes.func,
  removeUser: PropTypes.func
};

WorkspaceUsersDialog.defaultProps = {
  workspace: null,
  fetchUsers: undefined,
  addUsers: undefined,
  removeUser: undefined
};

export default WorkspaceUsersDialog;
